import _ from 'lodash'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import {
  NotificationChannel,
  NotificationPreferences,
  PreferenceKey,
} from './types'

interface NotificationSettingsStore {
  formState: NotificationPreferences | null
  savedState: NotificationPreferences | null
  isDirty: boolean
  activeTab: string | null

  initializeState: (preferences: NotificationPreferences) => void
  updatePreference: (key: PreferenceKey, value: NotificationChannel) => void
  saveState: () => void
  cancelChanges: () => void
  setActiveTab: (tab: string | null) => void
  reset: () => void
}

const initialState = {
  formState: null as NotificationPreferences | null,
  savedState: null as NotificationPreferences | null,
  isDirty: false,
  activeTab: null as string | null,
}

/**
 * Helper function to check if form state differs from saved state
 */
const checkIsDirty = (
  formState: NotificationPreferences | null,
  savedState: NotificationPreferences | null
): boolean => {
  if (!formState || !savedState) return false

  return !_.isEqual(formState, savedState)
}

export const useNotificationSettingsStore = create<NotificationSettingsStore>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Initialize form state with preferences from API
       * Sets both formState and savedState to the same values
       * Clears dirty flag
       */
      initializeState: (preferences: NotificationPreferences) => {
        set(
          {
            formState: { ...preferences },
            savedState: { ...preferences },
            isDirty: false,
          },
          false,
          'initializeState'
        )
      },

      /**
       * Update a single preference value
       * Recalculates isDirty by comparing with savedState
       */
      updatePreference: (key: PreferenceKey, value: NotificationChannel) => {
        set(
          (state) => {
            if (!state.formState) return state

            const newFormState = {
              ...state.formState,
              [key]: value,
            }

            return {
              formState: newFormState,
              isDirty: checkIsDirty(newFormState, state.savedState),
            }
          },
          false,
          'updatePreference'
        )
      },

      /**
       * Save current form state
       * Copies formState to savedState and clears dirty flag
       * Called after successful API update
       */
      saveState: () => {
        set(
          (state) => ({
            savedState: state.formState ? { ...state.formState } : null,
            isDirty: false,
          }),
          false,
          'saveState'
        )
      },

      /**
       * Cancel changes and revert to last saved state
       * Resets formState to savedState and clears dirty flag
       */
      cancelChanges: () => {
        set(
          (state) => ({
            formState: state.savedState ? { ...state.savedState } : null,
            isDirty: false,
          }),
          false,
          'cancelChanges'
        )
      },

      /**
       * Set active tab for category navigation
       */
      setActiveTab: (tab: string | null) => {
        set({ activeTab: tab }, false, 'setActiveTab')
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    {
      name: 'NotificationSettingsStore',
    }
  )
)
